import React, { useRef, useState, useEffect } from 'react';
import { CloudArrowUpIcon, XMarkIcon, PhotoIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const FileUpload = ({
  label,
  accept = 'image/*',
  maxSize = 2, // MB - reduced from 5MB to 2MB for cover images
  minWidth = 800, // Minimum width for images
  minHeight = 400, // Minimum height for images
  recommendedWidth = 1200, // Recommended width
  recommendedHeight = 630, // Recommended height (Facebook OG standard)
  onChange,
  onMetadataChange, // Callback for alt text and other metadata
  value,
  preview,
  error,
  className = '',
  showAltText = false, // Show alt text field for cover images
  altText = '',
}) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(preview || null);
  const [imageMetadata, setImageMetadata] = useState({ width: 0, height: 0, size: 0 });
  const [localAltText, setLocalAltText] = useState(altText || '');
  const [validationWarning, setValidationWarning] = useState('');

  useEffect(() => {
    setLocalAltText(altText || '');
  }, [altText]);

  const validateImage = (file) => {
    return new Promise((resolve, reject) => {
      // Check file size
      if (file.size > maxSize * 1024 * 1024) {
        reject(`File size must be less than ${maxSize}MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
        return;
      }

      // Check if it's an image
      if (!file.type.startsWith('image/')) {
        reject('Please select an image file (PNG, JPG, WebP)');
        return;
      }

      // Check image dimensions
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        
        const width = img.width;
        const height = img.height;
        const size = file.size;

        setImageMetadata({ width, height, size });

        // Validate dimensions
        if (minWidth && width < minWidth) {
          setValidationWarning(`⚠️ Image width (${width}px) is below recommended minimum (${minWidth}px)`);
        } else if (minHeight && height < minHeight) {
          setValidationWarning(`⚠️ Image height (${height}px) is below recommended minimum (${minHeight}px)`);
        } else if (recommendedWidth && recommendedHeight && (width !== recommendedWidth || height !== recommendedHeight)) {
          setValidationWarning(`💡 Recommended size: ${recommendedWidth}x${recommendedHeight}px. Current: ${width}x${height}px`);
        } else {
          setValidationWarning('');
        }

        resolve({ width, height, size });
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject('Failed to load image. Please try another file.');
      };

      img.src = objectUrl;
    });
  };

  const handleFileChange = async (file) => {
    if (!file) return;

    try {
      await validateImage(file);
      onChange(file);

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setPreviewUrl(e.target.result);
        reader.readAsDataURL(file);
      }
    } catch (error) {
      alert(error);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileChange(file);
  };

  const handleRemove = () => {
    onChange(null);
    setPreviewUrl(null);
    setImageMetadata({ width: 0, height: 0, size: 0 });
    setValidationWarning('');
    setLocalAltText('');
    if (inputRef.current) inputRef.current.value = '';
    if (onMetadataChange) {
      onMetadataChange({ altText: '' });
    }
  };

  const handleAltTextChange = (e) => {
    const newAltText = e.target.value;
    setLocalAltText(newAltText);
    if (onMetadataChange) {
      onMetadataChange({ altText: newAltText });
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {label && <label className="block text-sm font-medium text-slate-700">{label}</label>}
      
      {previewUrl || preview ? (
        <div className="space-y-3">
          <div className="relative inline-block">
            <img
              src={previewUrl || preview}
              alt={localAltText || "Preview"}
              className="w-full max-w-md h-auto object-cover rounded-xl border-2 border-slate-200"
            />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Image Metadata */}
          {imageMetadata.width > 0 && (
            <div className="text-xs text-slate-600 space-y-1 bg-slate-50 p-3 rounded-lg">
              <div className="flex items-center gap-2">
                <PhotoIcon className="w-4 h-4" />
                <span className="font-medium">Image Info:</span>
              </div>
              <div className="ml-6 space-y-0.5">
                <p>📐 Dimensions: {imageMetadata.width} × {imageMetadata.height}px</p>
                <p>💾 Size: {(imageMetadata.size / 1024).toFixed(2)} KB</p>
                {recommendedWidth && recommendedHeight && (
                  <p className="text-slate-500">✨ Recommended: {recommendedWidth} × {recommendedHeight}px</p>
                )}
              </div>
            </div>
          )}

          {/* Validation Warning */}
          {validationWarning && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-200">
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{validationWarning}</span>
            </div>
          )}

          {/* Alt Text Field */}
          {showAltText && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Alt Text (for SEO & Accessibility) *
              </label>
              <input
                type="text"
                value={localAltText}
                onChange={handleAltTextChange}
                placeholder="Describe this image for screen readers and SEO"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                maxLength={125}
              />
              <div className="flex justify-between mt-1">
                <p className="text-xs text-slate-500">
                  Describe what's in the image (important for accessibility)
                </p>
                <span className="text-xs text-slate-400">{localAltText.length}/125</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          className={`upload-area ${dragOver ? 'dragover' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <CloudArrowUpIcon className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600 font-medium mb-1">
            Drop your file here, or <span className="text-indigo-600 cursor-pointer">browse</span>
          </p>
          <p className="text-sm text-slate-400 mb-2">
            {accept === 'image/*' ? 'PNG, JPG, WebP' : accept} up to {maxSize}MB
          </p>
          {recommendedWidth && recommendedHeight && (
            <p className="text-xs text-slate-500">
              📐 Recommended: {recommendedWidth} × {recommendedHeight}px
            </p>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => handleFileChange(e.target.files[0])}
        className="hidden"
      />

      {error && <p className="text-sm text-red-500 flex items-center gap-1">
        <ExclamationTriangleIcon className="w-4 h-4" />
        {error}
      </p>}
    </div>
  );
};

export default FileUpload;
